import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { MapPin, Wind, Thermometer } from "lucide-react";
import { GrayBase, useCounties, project, W, H, liveLoopFrames } from "@/components/broadcast/TvMap";
import { iconFor } from "@/components/broadcast/shared";

// Poll the active town spotlight (admin-fired now; YouTube-chat-fired later).
export function useSpotlight() {
  const [sp, setSp] = useState(null);
  useEffect(() => {
    const check = () => api.get("/spotlight/active").then((r) => {
      const a = r.data?.active;
      if (!a) { setSp(null); return; }
      setSp((cur) => (cur && cur.id === a.id ? cur : { ...a, endsAt: Date.now() + Math.max(1000, (a.remaining_s || 0) * 1000) }));
    }).catch(() => {});
    check();
    const t = setInterval(check, 5000);
    return () => clearInterval(t);
  }, []);
  return sp;
}

// Radar zoomed to the town, with a pulsing pin at its coordinates.
function ZoomRadar({ lat, lon }) {
  const counties = useCounties();
  const frame = liveLoopFrames();
  const latestKey = frame[frame.length - 1]?.key;
  const { x, y } = project(lat, lon);
  const vw = W / 2.8, vh = H / 2.8;
  const vx = Math.max(0, Math.min(W - vw, x - vw / 2));
  const vy = Math.max(0, Math.min(H - vh, y - vh / 2));
  return (
    <svg viewBox={`${vx} ${vy} ${vw} ${vh}`} className="h-full w-full" preserveAspectRatio="xMidYMid slice" data-testid="spotlight-radar">
      <GrayBase counties={counties} strokeWidth={2.2}>
        {latestKey && <image href={`${API}/radar/frame?mode=live&key=${latestKey}`} x="0" y="0" width={W} height={H} preserveAspectRatio="none" opacity="0.92" />}
        <g>
          <circle cx={x} cy={y} r="30" fill="none" stroke="#ffd60a" strokeWidth="4" opacity="0.9" />
          <circle cx={x} cy={y} r="13" fill="#d90429" stroke="#fff" strokeWidth="4" />
        </g>
      </GrayBase>
    </svg>
  );
}

export function TownSpotlightSlide({ spotlight }) {
  if (!spotlight) return null;
  const c = spotlight.conditions || {};
  const temp = c.temp != null ? `${Math.round(c.temp)}°` : "—";
  const wind = c.wind || (c.wind_speed_mph != null ? `${c.wind_speed_mph} mph` : "Calm");
  return (
    <div data-testid="town-spotlight-slide" className="relative h-full w-full overflow-hidden bg-[#05070d]">
      {spotlight.lat != null && spotlight.lon != null && <div className="absolute inset-0"><ZoomRadar lat={spotlight.lat} lon={spotlight.lon} /></div>}

      {/* Header badge */}
      <div className="absolute left-[46px] top-[34px] z-20 flex items-center gap-2 rounded-full bg-[#2f7bed] px-5 py-2 font-oswald text-[20px] font-black uppercase tracking-[0.2em] text-white shadow-[0_6px_20px_rgba(0,0,0,0.5)]" data-testid="spotlight-badge">
        <MapPin className="h-6 w-6" strokeWidth={2.6} /> GAWX Town Shoutout
      </div>

      {/* Bottom glass bar: shoutout + conditions */}
      <div className="absolute inset-x-0 bottom-0 z-20" style={{ background: "linear-gradient(0deg, rgba(5,7,13,0.96) 8%, rgba(5,7,13,0.78) 60%, rgba(5,7,13,0) 100%)" }}>
        <div className="flex items-end justify-between gap-8 px-[52px] pb-[46px] pt-[80px]">
          <div className="min-w-0 flex-1">
            <div className="font-oswald text-[26px] font-bold uppercase tracking-[0.16em] text-[#ffd60a]">Right Now In</div>
            <div className="truncate font-oswald text-[84px] font-black uppercase leading-[0.9] text-white" data-testid="spotlight-place">{spotlight.place}</div>
            <div className="mt-2 truncate font-plex text-[30px] font-semibold text-white/90" data-testid="spotlight-shoutout">{spotlight.shoutout}</div>
          </div>
          <div className="flex shrink-0 items-stretch gap-4">
            <div className="flex min-w-[190px] flex-col items-center justify-center rounded-[14px] bg-white/[0.08] px-6 py-4 ring-1 ring-white/15">
              <div className="flex items-center gap-3">
                {iconFor(c.condition || "", "h-[52px] w-[52px]")}
                <span className="font-oswald text-[72px] font-black leading-none text-white" data-testid="spotlight-temp">{temp}</span>
              </div>
              <span className="mt-1 truncate font-oswald text-[22px] font-bold uppercase text-[#c3ccd8]">{c.condition || "—"}</span>
            </div>
            <div className="flex min-w-[170px] flex-col justify-center gap-3 rounded-[14px] bg-white/[0.08] px-6 py-4 ring-1 ring-white/15 font-oswald uppercase text-white">
              <div className="flex items-center gap-3 text-[24px] font-bold"><Wind className="h-7 w-7 text-[#7fb2ff]" strokeWidth={2.2} /> {wind}</div>
              <div className="flex items-center gap-3 text-[24px] font-bold"><Thermometer className="h-7 w-7 text-[#ff8f6b]" strokeWidth={2.2} /> Feels {c.feels_like != null ? `${Math.round(c.feels_like)}°` : temp}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
